import type { JSONContent } from "@tiptap/react";
import type { SubTemplateData } from "@/types/evaluation";
import { nodeTextContent, stripMarkdown, renderField } from "@/components/unified/renderField";
import { parseFieldDef } from "@/lib/parseFieldDef";

// Re-export so EditorPage imports don't need to change
export { buildReportLineMap } from "@/components/unified/buildReportLineMap";

interface ReportSection {
  sectionId?: string;
  lines: string[];
}

interface SectionBuildState {
  current: ReportSection;
  readonly sections: ReportSection[];
  currentSectionVisible: boolean;
  afterHidden: boolean;
}

function handleSectionNode(
  node: JSONContent,
  state: SectionBuildState,
  sectionToggles: Record<string, boolean>,
): void {
  // Section nodes control visibility but do not render in report
  if (state.current.lines.length > 0 || state.current.sectionId) {
    state.sections.push(state.current);
  }
  const sectionId = node.attrs?.sectionId as string | undefined;
  state.currentSectionVisible = sectionId ? (sectionToggles[sectionId] ?? true) : true;
  state.current = { lines: [], sectionId };
  state.afterHidden = true;
}

function handleHeadingNode(node: JSONContent, state: SectionBuildState): void {
  // Visual headings pass through as text
  if (!state.currentSectionVisible) {
    return;
  }
  const text = nodeTextContent(node);
  if (text) {
    state.current.lines.push(text);
  }
  state.afterHidden = false;
}

function handleParagraphNode(
  node: JSONContent,
  state: SectionBuildState,
  interpolate: (s: string) => string,
): void {
  if (!state.currentSectionVisible) {
    return;
  }
  const text = nodeTextContent(node);
  // Skip empty paragraphs immediately after hidden directives (@section, @scoregroup)
  if (!text && state.afterHidden) {
    state.afterHidden = false;
    return;
  }
  state.current.lines.push(text ? interpolate(stripMarkdown(text)) : "");
  state.afterHidden = false;
}

function handleFieldNode(
  node: JSONContent,
  state: SectionBuildState,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  interpolate: (s: string) => string,
  fieldToggles: Record<string, boolean>,
): void {
  if (!state.currentSectionVisible) {
    return;
  }
  const field = parseFieldDef((node.attrs?.fieldDef as string | undefined) ?? "");
  if (!field) {
    return;
  }
  if (fieldToggles[field.id] === false) {
    state.afterHidden = true;
    return;
  }
  const rendered = renderField(field, values[field.id], subTemplateValues[field.id], interpolate);
  if (rendered) {
    state.current.lines.push(...rendered.split("\n"));
  }
  state.afterHidden = false;
}

export function documentToReportSections(
  doc: JSONContent,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  interpolate: (s: string) => string,
  sectionToggles: Record<string, boolean> = {},
  fieldToggles: Record<string, boolean> = {},
): ReportSection[] {
  const state: SectionBuildState = {
    afterHidden: false,
    current: { lines: [] },
    currentSectionVisible: true,
    sections: [],
  };

  for (const node of doc.content ?? []) {
    switch (node.type) {
      case "section": {
        handleSectionNode(node, state, sectionToggles);
        break;
      }
      case "heading": {
        handleHeadingNode(node, state);
        break;
      }
      case "scoregroupNode": {
        // Score group marker — metadata only, not rendered in report
        state.afterHidden = true;
        break;
      }
      case "paragraph": {
        handleParagraphNode(node, state, interpolate);
        break;
      }
      case "fieldNode": {
        handleFieldNode(node, state, values, subTemplateValues, interpolate, fieldToggles);
        break;
      }
      // No default
    }
  }
  if (state.current.lines.length > 0 || state.current.sectionId) {
    state.sections.push(state.current);
  }

  return state.sections;
}

export function documentToReport(
  doc: JSONContent,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  interpolate: (s: string) => string,
  sectionToggles: Record<string, boolean> = {},
  fieldToggles: Record<string, boolean> = {},
): string {
  return (
    documentToReportSections(
      doc,
      values,
      subTemplateValues,
      interpolate,
      sectionToggles,
      fieldToggles,
    )
      .flatMap((s) => s.lines)
      .join("\n")
      .trimEnd() + "\n"
  );
}
